/**
 * Semantic analytics event taxonomy. One product-flavoured event name per
 * meaningful conversion moment, fired through {@link trackEvent} which fans it
 * out to GA4 and Facebook Pixel.
 *
 * Event names are deliberately product-flavoured (upload / trial / signup) and
 * MUST NOT reveal the AI/STT tech stack (no provider or model names) — see the
 * no-leak guard in batch-transcription-page.test.tsx.
 */
export const ANALYTICS_EVENTS = {
  /** Hero "download" CTA clicked (smooth-scrolls to the #download section). Carries `location` (`hero`). */
  landingDownloadCtaClick: 'landing_download_cta_click',
  /** Real app download link clicked. Carries `platform` (macos/windows/android/ios) + `location` (`download`/`footer`). */
  downloadAppClick: 'download_app_click',
  /** Visitor took over the hero live demo (clicked the mic to actually try it) — the strongest "interested" signal on the landing page. */
  heroDemoInteract: 'hero_demo_interact',
  /** The hero live demo produced a real transcript for the visitor (the "aha" moment). */
  heroDemoResultShown: 'hero_demo_result_shown',
  /** Hero "try the demo" CTA clicked — the assist that routes attention to the live demo. Carries `location` (`hero`). */
  landingTryDemoClick: 'landing_try_demo_click',
  /** Pricing plan card body clicked (highlights/considers a plan — softer than the CTA). Carries `plan` + `cycle`. */
  pricingPlanCardClick: 'pricing_plan_card_click',
  /** Pricing billing-cycle toggle flipped. Carries `cycle`. */
  pricingCycleToggle: 'pricing_cycle_toggle',
  /** Pricing free-plan CTA clicked (routes to the free trial). */
  pricingFreeCtaClick: 'pricing_free_cta_click',
  /** Pricing credit-pack CTA clicked. */
  pricingCreditPackCtaClick: 'pricing_credit_pack_cta_click',
  /** Pricing team CTA clicked. */
  pricingTeamCtaClick: 'pricing_team_cta_click',
  /**
   * Guest opened the OS file picker. Carries `source` (button/dropzone).
   *
   * `trialUploadStarted` only fires once a file has passed format + duration
   * validation, so on its own it cannot tell "never opened the picker" (no
   * file to hand) from "opened it and picked nothing" from "picked something
   * that got rejected". This event and the two below split those apart, which
   * is what locates the drop-off before anyone redesigns the page.
   */
  trialFilePickerOpened: 'trial_file_picker_opened',
  /** Guest actually chose a file — fired before any validation runs. */
  trialFileSelected: 'trial_file_selected',
  /**
   * The chosen file was rejected — by the browser's own checks or by the
   * server's authoritative ones. Carries `reason` (unsupported_format /
   * duration_unreadable / file_too_large).
   *
   * A rejection is the visitor being told no about the FILE, which is a
   * different problem from the upload breaking: it is answered by changing what
   * we accept or how we say so, never by fixing an outage. `trial_upload_failed`
   * is the other half, and no attempt reports both.
   */
  trialFileRejected: 'trial_file_rejected',
  /** Guest started a free trial upload (valid file, within the trial cap). */
  trialUploadStarted: 'trial_upload_started',
  /**
   * An accepted file's upload never reached the result page — the missing half
   * of `trial_upload_started`, which until now was followed by silence whenever
   * anything went wrong below the two rejection branches.
   *
   * It answers "of the people who committed to a real upload, how many got
   * nothing, and was it us or the road" — one row per failed ATTEMPT, so a
   * visitor who retries three times is three rows and the retry count is itself
   * the signal. A cancel is not a failure and reports nothing.
   *
   * `reason` is the whole set: `rate_limited`, `trial_disabled`,
   * `upload_stalled`, `upload_not_found`, `network`, `server_error`,
   * `unexpected_response`, `other` — classified once in
   * `features/anonymous-trial/upload-failure-reason.ts` and shared with the
   * Sentry report fired beside it. Also carries `status` (when there was one),
   * `code` and `phase`. Never a filename.
   *
   * CUSTOM, never standard: this measures a breakage, and handing it to Meta as
   * a conversion would optimise ad delivery toward people whose uploads fail.
   */
  trialUploadFailed: 'trial_upload_failed',
  /**
   * The guest's upload finished transcribing while they were still on the page
   * that started it — the third state beside `trial_upload_started` and
   * `trial_upload_failed`, so the success rate no longer depends on whether the
   * visitor also clicked through to the result page (`trial_result_viewed`).
   *
   * Mirrored to `anonymous_events`: `anonymous_trials` has no `kv_did` to join
   * back to the browser and its rows are purged 24 hours after creation, so this
   * row is the only completion record that outlives the day. No params.
   */
  trialUploadCompleted: 'trial_upload_completed',
  /** Guest's trial transcript finished and is shown — top of the conversion funnel. */
  trialResultViewed: 'trial_result_viewed',
  /** Trial transcript downloaded (vtt/srt) — requires a session (post-signup). */
  trialTranscriptDownload: 'trial_transcript_download',
  /** Trial transcript copied to clipboard — requires a session (post-signup). */
  trialTranscriptCopy: 'trial_transcript_copy',
  /**
   * A /pricing link on the guest trial result page was clicked. Carries two
   * params, and BOTH are needed to read the numbers.
   *
   * `location` — which of the four entries on that ONE page was clicked. It is
   * the SAME param the download/demo CTAs use for this question (see
   * `landingDownloadCtaClick`, `downloadAppClick`, `landingTryDemoClick`
   * above), so cross-entry comparisons read one dimension, not one per event.
   * Values: `trim_notice` (beside the "only the first N minutes" line at the
   * top), `trim_end` (beside the end-of-transcript marker), `perk_strip` (the
   * page footer) and `signup_done` (the panel shown after in-page signup).
   *
   * `status` — the trial's status at click time. The four entries are NOT
   * equally exposed: `trim_notice` and `perk_strip` are already on screen while
   * the visitor is watching the poller (the trim length is stamped at submit
   * time, so the notice does not wait for the transcript), while `trim_end`
   * exists only once it completed and `signup_done` only after signup. That
   * asymmetry is a deliberate product choice — reading "only the first 5
   * minutes" while waiting is exactly when someone weighs a plan, so the entry
   * stays and the measurement adapts instead. Comparing raw `location` totals
   * therefore overstates the two always-on entries; hold `status` fixed
   * (`status=completed` is the fair field, where all four can appear) and
   * compare `location` within it.
   *
   * Why the event exists at all: /pricing's own `view_item_list` already counts
   * arrivals and GA's `page_referrer` narrows them to this page, but all four
   * entries share that one referrer, so neither can say WHICH entry sent them
   * — the whole question when deciding where a pricing entry belongs.
   */
  trialResultPricingClick: 'trial_result_pricing_click',
  /**
   * The magic link in that email was used: the address is now verified, the
   * account is provisioned, and the transcript is attached. This is the event
   * that separates a real lead from an address someone typed.
   */
  trialTranscriptEmailClaimed: 'trial_transcript_email_claimed',
  /**
   * The link signed them in but attaching the transcript failed (expired trial,
   * already claimed, API down). Without its own event the claim funnel only
   * ever reports successes, so a systematic breakage would look like nobody
   * clicking rather than everybody failing.
   */
  trialTranscriptEmailClaimFailed: 'trial_transcript_email_claim_failed',
  /**
   * A campaign panel came into view. The DENOMINATOR of the campaign funnel:
   * without it, a campaign with few claims is indistinguishable from one nobody
   * scrolled far enough to see. Carries `slug`, `surface` and `state` — the
   * same panel converts very differently when it reads 額滿 rather than 開放中.
   */
  campaignViewed: 'campaign_viewed',
  /** Visitor's address passed format validation and the OTP modal opened. Carries `slug`, `surface`. */
  campaignEmailSubmitted: 'campaign_email_submitted',
  /**
   * The grant landed. Carries `slug`, `surface`, `path` (otp/signed_in) and
   * `account_created`; the split is what tells a campaign that ACQUIRES from
   * one that only re-activates the people already on the list.
   */
  campaignClaimed: 'campaign_claimed',
  /**
   * The claim call failed. Carries `reason` (the API's error code). A campaign
   * that fills up, or one breaking for everyone, looks identical from the
   * success events alone — both simply stop producing claims.
   */
  campaignClaimFailed: 'campaign_claim_failed',
  /** Waitlist CTA on a full/capped campaign clicked. Carries `slug`, `surface`. */
  campaignWaitlistClick: 'campaign_waitlist_click',
  /** Guest hit a gated action and got nudged toward signup. */
  signupNudgeShown: 'signup_nudge_shown',
  /** Guest submitted their email in the in-page signup (OTP requested). */
  signupEmailSubmitted: 'signup_email_submitted',
  /** Guest completed signup (OTP verified) — the key conversion event. */
  signupCompleted: 'signup_completed',
  /**
   * A link or CTA on the marketing site was clicked. One event for every
   * in-site navigation so "which entry sends people where" is a single query:
   * `location` is where on the page it sat (`header`, `header_products`,
   * `mobile_menu`, `footer`, `hero`, `end_cta`, `faq`, or a section's
   * `trackingKey` such as `services`), `target` is where it goes (`/transcription`,
   * `/transcription#try`, `#try`). The page is GA4's own `page_location`.
   */
  siteLinkClicked: 'site_link_clicked',
  /**
   * A marketing section was seen — at least half of it on screen, or it filled
   * half the screen (a section taller than two screens can never be half
   * visible), continuously for one second so a section a smooth scroll carries
   * past does not count — once per section per page view. Carries `location` (the section's `trackingKey`), the same value the
   * clicks inside that section report, so "saw it" and "clicked in it" join on
   * one field. It is the denominator of every entry's click-through rate.
   */
  siteSectionViewed: 'site_section_viewed',
  /**
   * The visitor first operated a product demo on a marketing page — once per
   * demo per page view. Carries `demo` (`subtitle`, `meeting_editor`,
   * `meeting_outputs`, `notes_templates`). The dictation demo keeps its older
   * `hero_demo_interact`.
   */
  productDemoStarted: 'product_demo_started',
  /**
   * A FAQ entry was expanded (not collapsed). Carries `question`, the entry's
   * fixed key — never its text — so a copy edit does not split the series.
   */
  faqOpened: 'faq_opened',
  /**
   * A/B test exposure — the visitor was actually assigned a variant. Fired by
   * GrowthBook's trackingCallback and carries `experiment_id` + `variation_id`;
   * those exact field names are what GrowthBook's GA4 data source reads, so
   * don't rename them. This is the DENOMINATOR of every experiment result.
   */
  experimentViewed: 'experiment_viewed',
  /**
   * A new member answered the「你最想先用 KIAO Voice 做什麼？」question on the
   * empty transcripts list. Carries `use_case` (`meeting`, `lecture`, `media`,
   * `dictation`, `other`). The server stores the same answer on the account;
   * this event is what lets GA4 put the answer next to the upload and signup
   * events that follow it.
   */
  useCaseSelected: 'use_case_selected',
  /** The member skipped that question instead of answering it. No params. */
  useCaseQuestionDismissed: 'use_case_question_dismissed',
} as const

export type AnalyticsEventName = (typeof ANALYTICS_EVENTS)[keyof typeof ANALYTICS_EVENTS]

/**
 * The only actions an event name may end with. Naming rules, and what each
 * action means, live in `.claude/skills/event-conventions/SKILL.md`;
 * `events.test.ts` enforces them for every name not in
 * {@link LEGACY_EVENT_NAMES}.
 */
export const ANALYTICS_EVENT_VERBS = [
  'viewed',
  'clicked',
  'opened',
  'dismissed',
  'selected',
  'toggled',
  'submitted',
  'started',
  'completed',
  'failed',
  'rejected',
  'copied',
  'downloaded',
  'claimed',
] as const

/**
 * Names that shipped before the naming rules and are kept as they are:
 * renaming one would cut its GA4 history, the `anonymous_events` rows and the
 * funnel nodes built on it. New events must not copy them. When one is
 * retired, it leaves this set in the same change (a test checks that every
 * entry is still a live event).
 */
export const LEGACY_EVENT_NAMES: ReadonlySet<AnalyticsEventName> = new Set<AnalyticsEventName>([
  'landing_download_cta_click',
  'download_app_click',
  'hero_demo_interact',
  'hero_demo_result_shown',
  'landing_try_demo_click',
  'pricing_plan_card_click',
  'pricing_cycle_toggle',
  'pricing_free_cta_click',
  'pricing_credit_pack_cta_click',
  'pricing_team_cta_click',
  'trial_transcript_download',
  'trial_transcript_copy',
  'trial_result_pricing_click',
  'campaign_waitlist_click',
  'signup_nudge_shown',
])

/**
 * GA4's reserved event names (support.google.com/analytics/answer/13316687).
 * `click`, `scroll`, `file_download` and `form_start` are also sent by the
 * property's enhanced measurement, so reusing one would merge our event into
 * GA4's own.
 */
export const GA4_RESERVED_EVENT_NAMES: ReadonlySet<string> = new Set([
  'ad_impression',
  'app_remove',
  'app_store_refund',
  'app_store_subscription_cancel',
  'app_store_subscription_renew',
  'click',
  'error',
  'file_download',
  'first_open',
  'first_visit',
  'form_start',
  'form_submit',
  'in_app_purchase',
  'page_view',
  'scroll',
  'session_start',
  'user_engagement',
  'video_complete',
  'view_complete',
  'video_progress',
  'video_start',
  'view_search_results',
])

/**
 * Facebook Pixel mapping for each semantic event.
 * - `standard: true`  → `fbq('track', name)` (a Pixel standard event Meta can
 *   optimise ad delivery against, e.g. Lead / CompleteRegistration / ViewContent).
 * - `standard: false` → `fbq('trackCustom', name)` (a custom event).
 */
export interface PixelEventMapping {
  name: string
  standard: boolean
}

export const PIXEL_EVENT_MAP: Record<AnalyticsEventName, PixelEventMapping> = {
  [ANALYTICS_EVENTS.landingDownloadCtaClick]: { name: 'LandingDownloadCtaClick', standard: false },
  [ANALYTICS_EVENTS.downloadAppClick]: { name: 'DownloadApp', standard: false },
  [ANALYTICS_EVENTS.heroDemoInteract]: { name: 'HeroDemoInteract', standard: false },
  [ANALYTICS_EVENTS.heroDemoResultShown]: { name: 'HeroDemoResultShown', standard: false },
  [ANALYTICS_EVENTS.landingTryDemoClick]: { name: 'LandingTryDemoClick', standard: false },
  [ANALYTICS_EVENTS.pricingPlanCardClick]: { name: 'PricingPlanCardClick', standard: false },
  [ANALYTICS_EVENTS.pricingCycleToggle]: { name: 'PricingCycleToggle', standard: false },
  [ANALYTICS_EVENTS.pricingFreeCtaClick]: { name: 'PricingFreeCtaClick', standard: false },
  [ANALYTICS_EVENTS.pricingCreditPackCtaClick]: {
    name: 'PricingCreditPackCtaClick',
    standard: false,
  },
  [ANALYTICS_EVENTS.pricingTeamCtaClick]: { name: 'PricingTeamCtaClick', standard: false },
  // Diagnostic funnel events — deliberately custom: they measure friction, not
  // conversion, so Meta must not optimise delivery toward them.
  [ANALYTICS_EVENTS.trialFilePickerOpened]: { name: 'TrialFilePickerOpened', standard: false },
  [ANALYTICS_EVENTS.trialFileSelected]: { name: 'TrialFileSelected', standard: false },
  [ANALYTICS_EVENTS.trialFileRejected]: { name: 'TrialFileRejected', standard: false },
  [ANALYTICS_EVENTS.trialUploadStarted]: { name: 'TrialUploadStarted', standard: false },
  // Custom, not standard: a failed upload is a breakage. Feeding it to Meta as
  // a conversion would tell delivery to go find more people who will fail.
  [ANALYTICS_EVENTS.trialUploadFailed]: { name: 'TrialUploadFailed', standard: false },
  [ANALYTICS_EVENTS.trialUploadCompleted]: { name: 'TrialUploadCompleted', standard: false },
  [ANALYTICS_EVENTS.trialResultViewed]: { name: 'ViewContent', standard: true },
  [ANALYTICS_EVENTS.trialTranscriptDownload]: { name: 'TrialTranscriptDownload', standard: false },
  [ANALYTICS_EVENTS.trialTranscriptCopy]: { name: 'TrialTranscriptCopy', standard: false },
  // Custom, not standard: looking at the price list is navigation, not a
  // purchase. Handing Meta a conversion here would optimise delivery toward
  // people who read prices and leave — the cheapest half of the funnel.
  [ANALYTICS_EVENTS.trialResultPricingClick]: {
    name: 'TrialResultPricingClick',
    standard: false,
  },
  // Verified + provisioned — the same milestone as finishing OTP signup.
  [ANALYTICS_EVENTS.trialTranscriptEmailClaimed]: {
    name: 'CompleteRegistration',
    standard: true,
  },
  // Custom, not standard: this measures a breakage, and feeding it to Meta as a
  // conversion would optimise delivery toward people whose claims fail.
  [ANALYTICS_EVENTS.trialTranscriptEmailClaimFailed]: {
    name: 'TrialTranscriptEmailClaimFailed',
    standard: false,
  },
  // Custom: an impression is measurement, not something Meta should optimise
  // delivery toward.
  [ANALYTICS_EVENTS.campaignViewed]: { name: 'CampaignViewed', standard: false },
  // A campaign address is a captured lead exactly like the other two paths, so
  // it feeds the same standard event and campaigns optimise across all of them.
  [ANALYTICS_EVENTS.campaignEmailSubmitted]: { name: 'Lead', standard: true },
  // StartTrial rather than CompleteRegistration: the account is only the
  // vehicle — what the visitor actually got is a free run of a paid plan, and
  // that is the audience worth optimising toward.
  [ANALYTICS_EVENTS.campaignClaimed]: { name: 'StartTrial', standard: true },
  // Custom, not standard: this measures a refusal, and handing it to Meta as a
  // conversion would optimise delivery toward people whose claims fail.
  [ANALYTICS_EVENTS.campaignClaimFailed]: { name: 'CampaignClaimFailed', standard: false },
  [ANALYTICS_EVENTS.campaignWaitlistClick]: { name: 'CampaignWaitlistClick', standard: false },
  [ANALYTICS_EVENTS.signupNudgeShown]: { name: 'SignupNudgeShown', standard: false },
  // Lead = email captured; CompleteRegistration = account created. Both are
  // standard Pixel events campaigns are typically optimised toward.
  [ANALYTICS_EVENTS.signupEmailSubmitted]: { name: 'Lead', standard: true },
  [ANALYTICS_EVENTS.signupCompleted]: { name: 'CompleteRegistration', standard: true },
  // Navigation and reading are measurement, not conversions Meta should
  // optimise delivery toward.
  [ANALYTICS_EVENTS.siteLinkClicked]: { name: 'SiteLinkClicked', standard: false },
  [ANALYTICS_EVENTS.siteSectionViewed]: { name: 'SiteSectionViewed', standard: false },
  [ANALYTICS_EVENTS.productDemoStarted]: { name: 'ProductDemoStarted', standard: false },
  [ANALYTICS_EVENTS.faqOpened]: { name: 'FaqOpened', standard: false },
  // Custom: an exposure is measurement, not a conversion Meta should optimise
  // ad delivery toward.
  [ANALYTICS_EVENTS.experimentViewed]: { name: 'ExperimentViewed', standard: false },
  // Custom: a self-reported intent from someone already signed up is a
  // segmentation fact, not a conversion Meta should optimise ad delivery toward.
  [ANALYTICS_EVENTS.useCaseSelected]: { name: 'UseCaseSelected', standard: false },
  [ANALYTICS_EVENTS.useCaseQuestionDismissed]: {
    name: 'UseCaseQuestionDismissed',
    standard: false,
  },
}
